import React, { useCallback, useContext, useState } from "react";
import {
  ConfirmContextValue,
  ConfirmOptions,
  ConfirmProviderProps,
} from "./Confirm.types";

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider(props: ConfirmProviderProps): JSX.Element {
  const { children, component: Component } = props;

  const [open, setOpen] = useState(false);

  const [options, setOptions] = useState<ConfirmOptions>({
    title: "",
    message: "",
    actions: [],
  });

  const shouldSkip = (options: ConfirmOptions) => {
    const { notShowAgainId } = options;
    return notShowAgainId && localStorage.getItem(notShowAgainId) === "true";
  };

  const getFirstPrimaryAction = (options: ConfirmOptions) => {
    const { actions } = options;
    return actions.find((action) => action.primary);
  };

  const showConfirm = useCallback((options: ConfirmOptions) => {
    const primaryAction = getFirstPrimaryAction(options);
    if (shouldSkip(options) && primaryAction?.onClick) {
      primaryAction.onClick();
    } else {
      setOpen(true);
      setOptions(options);
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <ConfirmContext.Provider value={showConfirm}>
      {children}
      <Component {...options} open={open} onClose={handleClose} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const contextValue = useContext(ConfirmContext);
  if (!contextValue) {
    throw new Error("Wrap with ConfirmProvider");
  }
  return contextValue;
}
